import { useCurrentStateAndParams } from "@uirouter/react";

import type { AiTrackerReportsBrandOverviewPeriodChangesResponse } from "@similarweb/pro-api-client";

import { translate } from "app/filters/i18nFilter";
import { navigateToReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { ILLMReportWidget, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { EAvailableTabs } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";

import { FiltersCard } from "./FiltersCard";
import { FiltersCardsGroup } from "./WhatChangedWidget.styled";

interface IWhatChangedWidgetProps extends ILLMReportWidget {
    periodChangesData?: AiTrackerReportsBrandOverviewPeriodChangesResponse | null;
    isLoading?: boolean;
}

const joinDomainFilter = (domains?: string[]): string | undefined => {
    const filtered = domains?.filter(Boolean);
    return filtered?.length ? filtered.join(",") : undefined;
};

export const WhatChangedWidget = ({ campaign, periodChangesData, isLoading }: IWhatChangedWidgetProps) => {
    const counts = periodChangesData
        ? {
              newPrompts: periodChangesData.newPromptsCount,
              lostPrompts: periodChangesData.lostPromptsCount,
              newCitations: periodChangesData.newCitationsCount,
              lostCitations: periodChangesData.lostCitationsCount,
              turnedPositive: periodChangesData.turnedPositiveCount,
              turnedNegative: periodChangesData.turnedNegativeCount,
          }
        : { newPrompts: 0, lostPrompts: 0, newCitations: 0, lostCitations: 0, turnedPositive: 0, turnedNegative: 0 };

    const llmBreakdown = periodChangesData?.llmBreakdown;

    const {
        params: { model },
    } = useCurrentStateAndParams();

    const navigateToPromptsWithFilter = (params: Record<string, string>, label: string, count: number) => {
        allTrackers.dispatchTrackEvent("What Changed", "click", `${label}/${count}`);
        navigateToReport(campaign, model as LLModel, EAvailableTabs.PROMPTS, params);
    };

    const navigateToSourcesWithFilter = (
        params: Record<string, string>,
        label: string,
        count: number,
        domains?: string[],
    ) => {
        allTrackers.dispatchTrackEvent("What Changed", "click", `${label}/${count}`);
        const domainFilter = joinDomainFilter(domains);
        navigateToReport(campaign, model as LLModel, EAvailableTabs.SOURCES, {
            ...params,
            ...(domainFilter ? { domainFilter } : {}),
        });
    };

    return (
        <FiltersCardsGroup>
            <FiltersCard
                title={translate("llm.overview_report.what_changed.prompts.title")}
                subtitle={translate("llm.overview_report.what_changed.prompts.subtitle")}
                isLoading={isLoading}
                filters={[
                    {
                        type: "positive",
                        delta: counts.newPrompts,
                        text: translate("llm.overview_report.what_changed.new"),
                        tooltipTitle: translate("llm.overview_report.what_changed.tooltip.new_prompts"),
                        metricKey: "newPromptsCount",
                        llmBreakdown,
                        onFilter: () =>
                            navigateToPromptsWithFilter({ change: "New" }, "New Prompts", counts.newPrompts),
                    },
                    {
                        type: "negative",
                        delta: counts.lostPrompts,
                        text: translate("llm.overview_report.what_changed.lost"),
                        tooltipTitle: translate("llm.overview_report.what_changed.tooltip.lost_prompts"),
                        metricKey: "lostPromptsCount",
                        llmBreakdown,
                        onFilter: () =>
                            navigateToPromptsWithFilter({ change: "Lost" }, "Lost Prompts", counts.lostPrompts),
                    },
                ]}
            />
            {/*<FiltersCard
                title={translate("llm.overview_report.what_changed.citations.title")}
                subtitle={translate("llm.overview_report.what_changed.citations.subtitle")}
                isLoading={isLoading}
                filters={[
                    {
                        type: "positive",
                        delta: counts.newCitations,
                        text: translate("llm.overview_report.what_changed.new"),
                        tooltipTitle: translate("llm.overview_report.what_changed.tooltip.new_citations"),
                        metricKey: "newCitationsCount",
                        llmBreakdown,
                        onFilter: () =>
                            navigateToSourcesWithFilter(
                                { changeFilter: "New" },
                                "New Citations",
                                counts.newCitations,
                                periodChangesData?.newCitationsDomains,
                            ),
                    },
                    {
                        type: "negative",
                        delta: counts.lostCitations,
                        text: translate("llm.overview_report.what_changed.lost"),
                        tooltipTitle: translate("llm.overview_report.what_changed.tooltip.lost_citations"),
                        metricKey: "lostCitationsCount",
                        llmBreakdown,
                        onFilter: () =>
                            navigateToSourcesWithFilter(
                                { changeFilter: "Lost" },
                                "Lost Citations",
                                counts.lostCitations,
                                periodChangesData?.lostCitationsDomains,
                            ),
                    },
                ]}
            />*/}
            <FiltersCard
                title={translate("llm.overview_report.what_changed.sentiment.title")}
                subtitle={translate("llm.overview_report.what_changed.sentiment.subtitle")}
                emptyText={translate("llm.overview_report.what_changed.no_sentiment_shifts")}
                isLoading={isLoading}
                filters={[
                    {
                        type: "positive",
                        delta: counts.turnedPositive,
                        text: translate("llm.overview_report.what_changed.turned_positive"),
                        tooltipTitle: translate("llm.overview_report.what_changed.tooltip.turned_positive"),
                        metricKey: "turnedPositiveCount",
                        llmBreakdown,
                        onFilter: () =>
                            navigateToPromptsWithFilter(
                                { sentimentShift: "Turned Positive" },
                                "Turned Positive",
                                counts.turnedPositive,
                            ),
                    },
                    {
                        type: "negative",
                        delta: counts.turnedNegative,
                        text: translate("llm.overview_report.what_changed.turned_negative"),
                        tooltipTitle: translate("llm.overview_report.what_changed.tooltip.turned_negative"),
                        metricKey: "turnedNegativeCount",
                        llmBreakdown,
                        onFilter: () =>
                            navigateToPromptsWithFilter(
                                { sentimentShift: "Turned Negative" },
                                "Turned Negative",
                                counts.turnedNegative,
                            ),
                    },
                ]}
            />
        </FiltersCardsGroup>
    );
};
